

const fs = null;
const readline = null;

/* =========================================================
   CONSTANTS
========================================================= */

const MAX_LOOP_ITERATIONS = 1000000;
const MAX_CALL_DEPTH = 1000;

/* =========================================================
   ERROR TYPES
========================================================= */

class SceneError extends Error {
    constructor(message, token = null) {
        super(message);
        this.name = "SceneError";

        this.line = token?.line ?? null;
        this.column = token?.column ?? null;

        if (this.line !== null) {
            this.message =
                `${message}\n` +
                `Line ${this.line}, Column ${this.column}`;
        }
    }
}

class ReturnSignal {
    constructor(value) {
        this.value = value;
    }
}

class BreakSignal {}

class ContinueSignal {}

/* =========================================================
   TOKEN
========================================================= */

class Token {
    constructor(type, value, line, column) {
        this.type = type;
        this.value = value;
        this.line = line;
        this.column = column;
    }
}

/* =========================================================
   LEXER
========================================================= */

class Lexer {
    constructor(source) {
        this.source = source;
        this.pos = 0;
        this.line = 1;
        this.column = 1;
        this.tokens = [];
    }

    current() {
        return this.source[this.pos];
    }

    peek(offset = 1) {
        return this.source[this.pos + offset];
    }

    advance() {
        const ch = this.source[this.pos];

        if (ch === "\n") {
            this.line++;
            this.column = 1;
        } else {
            this.column++;
        }

        this.pos++;
        return ch;
    }

    add(type, value, line, column) {
        this.tokens.push(
            new Token(type, value, line, column)
        );
    }

    isAlpha(ch) {
        return !!ch && /[A-Za-z_]/.test(ch);
    }

    isDigit(ch) {
        return !!ch && /[0-9]/.test(ch);
    }

    isAlphaNumeric(ch) {
        return !!ch && /[A-Za-z0-9_]/.test(ch);
    }

    readString() {
        const startLine = this.line;
        const startColumn = this.column;

        const quote = this.advance();

        let value = "";

        while (this.pos < this.source.length) {
            const ch = this.current();

            if (ch === quote) {
                this.advance();

                this.add(
                    "string",
                    value,
                    startLine,
                    startColumn
                );

                return;
            }

            if (ch === "\\") {
                this.advance();

                if (this.pos >= this.source.length) {
                    throw new SceneError(
                        "AYYO GURU 😭 String incomplete.",
                        new Token(
                            "string",
                            "",
                            startLine,
                            startColumn
                        )
                    );
                }

                const escaped = this.advance();

                switch (escaped) {
                    case "n":
                        value += "\n";
                        break;

                    case "r":
                        value += "\r";
                        break;

                    case "t":
                        value += "\t";
                        break;

                    case "\\":
                        value += "\\";
                        break;

                    case "\"":
                        value += "\"";
                        break;

                    case "'":
                        value += "'";
                        break;

                    default:
                        value += escaped;
                        break;
                }

                continue;
            }

            value += this.advance();
        }

        throw new SceneError(
            "AYYO GURU 😭 String close maadilla.",
            new Token(
                "string",
                value,
                startLine,
                startColumn
            )
        );
    }

    readNumber() {
        const line = this.line;
        const column = this.column;

        let value = "";

        while (this.isDigit(this.current())) {
            value += this.advance();
        }

        if (
            this.current() === "." &&
            this.isDigit(this.peek())
        ) {
            value += this.advance();

            while (this.isDigit(this.current())) {
                value += this.advance();
            }
        }

        this.add(
            "number",
            Number(value),
            line,
            column
        );
    }

    readIdentifier() {
        const line = this.line;
        const column = this.column;

        let value = "";

        while (this.isAlphaNumeric(this.current())) {
            value += this.advance();
        }

        this.add(
            "identifier",
            value,
            line,
            column
        );
    }

    tokenize() {
        while (this.pos < this.source.length) {
            const ch = this.current();

            /* whitespace */

            if (
                ch === " " ||
                ch === "\t" ||
                ch === "\r" ||
                ch === "\n"
            ) {
                this.advance();
                continue;
            }

            /* comments */

            if (
                ch === "/" &&
                this.peek() === "/"
            ) {
                while (
                    this.pos < this.source.length &&
                    this.current() !== "\n"
                ) {
                    this.advance();
                }

                continue;
            }

            if (
                ch === "/" &&
                this.peek() === "*"
            ) {
                this.advance();
                this.advance();

                while (this.pos < this.source.length) {
                    if (
                        this.current() === "*" &&
                        this.peek() === "/"
                    ) {
                        this.advance();
                        this.advance();
                        break;
                    }

                    this.advance();
                }

                continue;
            }

            /* strings */

            if (ch === "\"" || ch === "'") {
                this.readString();
                continue;
            }

            /* numbers */

            if (this.isDigit(ch)) {
                this.readNumber();
                continue;
            }

            /* identifiers */

            if (this.isAlpha(ch)) {
                this.readIdentifier();
                continue;
            }

            const line = this.line;
            const column = this.column;

            const three =
                ch +
                (this.peek() || "") +
                (this.peek(2) || "");

            const two =
                ch +
                (this.peek() || "");

            /* three-character operators */

            if (
                three === "===" ||
                three === "!==" ||
                three === ">>>"
            ) {
                this.advance();
                this.advance();
                this.advance();

                this.add(
                    "operator",
                    three,
                    line,
                    column
                );

                continue;
            }

            /* two-character operators */

            const twoOperators = [
                "==",
                "!=",
                "<=",
                ">=",
                "&&",
                "||",
                "++",
                "--",
                "+=",
                "-=",
                "*=",
                "/=",
                "%=",
                "->",
                "<<",
                ">>",
                "**"
            ];

            if (twoOperators.includes(two)) {
                this.advance();
                this.advance();

                this.add(
                    "operator",
                    two,
                    line,
                    column
                );

                continue;
            }

            /* single-character */

            const singleOperators =
                "+-*/%=<>!?:.";

            const punctuation =
                "(){}[];,";
            
            if (
                singleOperators.includes(ch)
            ) {
                this.advance();

                this.add(
                    "operator",
                    ch,
                    line,
                    column
                );

                continue;
            }

            if (punctuation.includes(ch)) {
                this.advance();

                this.add(
                    "punctuation",
                    ch,
                    line,
                    column
                );

                continue;
            }

            throw new SceneError(
                `AYYO GURU 😭 Unknown character '${ch}'.`,
                new Token(
                    "unknown",
                    ch,
                    line,
                    column
                )
            );
        }

        this.tokens.push(
            new Token(
                "eof",
                "<EOF>",
                this.line,
                this.column
            )
        );

        return this.tokens;
    }
}

/* =========================================================
   PARSER
========================================================= */

class Parser {
    constructor(tokens) {
        this.tokens = tokens;
        this.current = 0;
    }

    peek(offset = 0) {
        return this.tokens[
            Math.min(
                this.current + offset,
                this.tokens.length - 1
            )
        ];
    }

    previous() {
        return this.tokens[this.current - 1];
    }

    atEnd() {
        return this.peek().type === "eof";
    }

    check(value) {
        return this.peek().value === value;
    }

    checkType(type) {
        return this.peek().type === type;
    }

    advance() {
        if (!this.atEnd()) {
            this.current++;
        }

        return this.previous();
    }

    match(...values) {
        for (const value of values) {
            if (this.check(value)) {
                this.advance();
                return true;
            }
        }

        return false;
    }

    expect(value, message) {
        if (this.check(value)) {
            return this.advance();
        }

        throw new SceneError(
            message ||
            `'${value}' beku guru.`,
            this.peek()
        );
    }

    expectIdentifier(message) {
        if (this.checkType("identifier")) {
            return this.advance();
        }

        throw new SceneError(
            message ||
            "Identifier beku guru.",
            this.peek()
        );
    }

    parseProgram() {
        const body = [];

        while (!this.atEnd()) {
            body.push(this.parseStatement());
        }

        return {
            type: "Program",
            body
        };
    }

    parseStatement() {
        /* empty ; */

        if (this.match(";")) {
            return {
                type: "EmptyStatement"
            };
        }

        /* block */

        if (this.match("{")) {
            return this.parseBlockAfterOpen();
        }

        /* declarations */

        if (this.match("idu")) {
            return this.parseVariableDeclaration(true);
        }

        /* struct */

        if (this.match("vastu")) {
            return this.parseStructDeclaration();
        }

        /* function */

        if (this.match("maadu")) {
            return this.parseFunctionDeclaration();
        }

        /* if */

        if (this.match("andre")) {
            return this.parseIfStatement();
        }

        /* while */

        if (this.match("aag")) {
            return this.parseWhileStatement();
        }

        /* for */

        if (this.match("ella")) {
            return this.parseForStatement();
        }

        /* do while */

        if (this.match("do")) {
            return this.parseDoWhileStatement();
        }

        /* return */

        if (this.match("sigutte")) {
            return this.parseReturnStatement();
        }

        /* break */

        if (this.match("mugisu")) {
            this.expect(
                ";",
                "';' beku guru."
            );

            return {
                type: "BreakStatement"
            };
        }

        /* continue */

        if (this.match("mundhe")) {
            this.expect(
                ";",
                "';' beku guru."
            );

            return {
                type: "ContinueStatement"
            };
        }

        /* expression */

        const expression =
            this.parseExpression();

        this.expect(
            ";",
            "';' beku guru."
        );

        return {
            type: "ExpressionStatement",
            expression
        };
    }

    parseBlockAfterOpen() {
        const body = [];

        while (
            !this.check("}") &&
            !this.atEnd()
        ) {
            body.push(
                this.parseStatement()
            );
        }

        this.expect(
            "}",
            "'}' beku guru."
        );

        return {
            type: "BlockStatement",
            body
        };
    }

    parseVariableDeclaration(requireSemicolon) {
        let declaredType = null;

        /*
         * Supported:
         *
         * idu number x
         * idu string x
         * idu array x
         * idu Node x
         * idu Node* x
         * idu Node& x
         * idu x
         */

        if (
            this.checkType("identifier") &&
            ![
                "=",
                ";",
                ")",
                ","
            ].includes(this.peek(1).value)
        ) {
            declaredType =
                this.advance().value;

            while (
                this.match("*", "&", "[")
            ) {
                if (
                    this.previous().value === "["
                ) {
                    this.expect(
                        "]",
                        "']' beku guru."
                    );
                }
            }
        }

        const name =
            this.expectIdentifier(
                "Variable hesaru beku guru."
            );

        let initializer = null;

        if (this.match("=")) {
            initializer =
                this.parseExpression();
        }

        if (requireSemicolon) {
            this.expect(
                ";",
                "';' beku guru."
            );
        }

        return {
            type: "VariableDeclaration",
            name: name.value,
            declaredType,
            initializer
        };
    }

    parseStructDeclaration() {
        const name =
            this.expectIdentifier(
                "vastu hesaru beku guru."
            );

        this.expect(
            "{",
            "'{' beku guru."
        );

        const fields = [];

        while (
            !this.check("}") &&
            !this.atEnd()
        ) {
            let fieldType = null;

            if (this.checkType("identifier")) {
                fieldType =
                    this.advance().value;
            }

            /*
             * Pointer/reference markers
             */

            while (
                this.match("*", "&")
            ) {}

            const fieldName =
                this.expectIdentifier(
                    "Struct field hesaru beku guru."
                );

            fields.push({
                name: fieldName.value,
                fieldType
            });

            this.expect(
                ";",
                "Struct field-ge ';' beku guru."
            );
        }

        this.expect(
            "}",
            "'}' beku guru."
        );

        this.match(";");

        return {
            type: "StructDeclaration",
            name: name.value,
            fields
        };
    }

    parseFunctionDeclaration() {
        const name =
            this.expectIdentifier(
                "Function hesaru beku guru."
            );

        this.expect(
            "(",
            "'(' beku guru."
        );

        const params = [];

        if (!this.check(")")) {
            do {
                let declaredType = null;

                /*
                 * Typed parameter:
                 * number x
                 * array values
                 * Node node
                 */

                if (
                    this.checkType("identifier") &&
                    this.peek(1).type === "identifier"
                ) {
                    declaredType =
                        this.advance().value;

                    while (
                        this.match("*", "&")
                    ) {}
                }

                const param =
                    this.expectIdentifier(
                        "Parameter hesaru beku guru."
                    );

                params.push({
                    name: param.value,
                    declaredType
                });

            } while (this.match(","));
        }

        this.expect(
            ")",
            "')' beku guru."
        );

        const body =
            this.parseStatement();

        return {
            type: "FunctionDeclaration",
            name: name.value,
            params,
            body
        };
    }

    parseIfStatement() {
        this.expect(
            "(",
            "'(' beku guru."
        );

        const condition =
            this.parseExpression();

        this.expect(
            ")",
            "')' beku guru."
        );

        const thenBranch =
            this.parseStatement();

        const elseIfBranches = [];
        let elseBranch = null;

        /*
         * Critical Scene++ grammar:
         *
         * illaandre (condition) { }
         *
         * illaandre { }
         *
         * illaandre andre (condition) { }
         */

        while (this.match("illaandre")) {

            /*
             * Backward compatibility:
             *
             * illaandre andre (...)
             */

            if (this.match("andre")) {
                this.expect(
                    "(",
                    "'(' beku guru."
                );

                const elseIfCondition =
                    this.parseExpression();

                this.expect(
                    ")",
                    "')' beku guru."
                );

                const body =
                    this.parseStatement();

                elseIfBranches.push({
                    condition: elseIfCondition,
                    body
                });

                continue;
            }

            /*
             * Normal:
             *
             * illaandre (...)
             */

            if (this.match("(")) {
                const elseIfCondition =
                    this.parseExpression();

                this.expect(
                    ")",
                    "')' beku guru."
                );

                const body =
                    this.parseStatement();

                elseIfBranches.push({
                    condition: elseIfCondition,
                    body
                });

                continue;
            }

            /*
             * Final:
             *
             * illaandre { ... }
             */

            elseBranch =
                this.parseStatement();

            break;
        }

        return {
            type: "IfStatement",
            condition,
            thenBranch,
            elseIfBranches,
            elseBranch
        };
    }

    parseWhileStatement() {
        this.expect(
            "(",
            "'(' beku guru."
        );

        const condition =
            this.parseExpression();

        this.expect(
            ")",
            "')' beku guru."
        );

        const body =
            this.parseStatement();

        return {
            type: "WhileStatement",
            condition,
            body
        };
    }

    parseForStatement() {
        this.expect(
            "(",
            "'(' beku guru."
        );

        let initializer = null;
        let condition = null;
        let increment = null;

        /*
         * Initializer
         */

        if (!this.check(";")) {
            if (this.match("idu")) {
                initializer =
                    this.parseVariableDeclaration(
                        false
                    );
            } else {
                initializer =
                    this.parseExpression();
            }
        }

        this.expect(
            ";",
            "for initializer-ge ';' beku guru."
        );

        /*
         * Condition
         */

        if (!this.check(";")) {
            condition =
                this.parseExpression();
        }

        this.expect(
            ";",
            "for condition-ge ';' beku guru."
        );

        /*
         * Increment
         */

        if (!this.check(")")) {
            increment =
                this.parseExpression();
        }

        this.expect(
            ")",
            "')' beku guru."
        );

        const body =
            this.parseStatement();

        return {
            type: "ForStatement",
            initializer,
            condition,
            increment,
            body
        };
    }

    parseDoWhileStatement() {
        const body =
            this.parseStatement();

        this.expect(
            "aag",
            "'aag' beku guru."
        );

        this.expect(
            "(",
            "'(' beku guru."
        );

        const condition =
            this.parseExpression();

        this.expect(
            ")",
            "')' beku guru."
        );

        this.expect(
            ";",
            "';' beku guru."
        );

        return {
            type: "DoWhileStatement",
            body,
            condition
        };
    }

    parseReturnStatement() {
        let value = null;

        if (!this.check(";")) {
            value =
                this.parseExpression();
        }

        this.expect(
            ";",
            "';' beku guru."
        );

        return {
            type: "ReturnStatement",
            value
        };
    }

    /* =====================================================
       EXPRESSIONS
    ===================================================== */

    parseExpression() {
        return this.parseAssignment();
    }

    parseAssignment() {
        let expression =
            this.parseTernary();

        const operators = [
            "=",
            "+=",
            "-=",
            "*=",
            "/=",
            "%="
        ];

        if (
            operators.includes(
                this.peek().value
            )
        ) {
            const operator =
                this.advance().value;

            const right =
                this.parseAssignment();

            expression = {
                type: "AssignmentExpression",
                operator,
                left: expression,
                right
            };
        }

        return expression;
    }

    parseTernary() {
        let expression =
            this.parseLogicalOr();

        if (this.match("?")) {
            const consequent =
                this.parseExpression();

            this.expect(
                ":",
                "':' beku guru."
            );

            const alternate =
                this.parseExpression();

            expression = {
                type: "TernaryExpression",
                condition: expression,
                consequent,
                alternate
            };
        }

        return expression;
    }

    parseLogicalOr() {
        let expression =
            this.parseLogicalAnd();

        while (this.match("||")) {
            const operator =
                this.previous().value;

            const right =
                this.parseLogicalAnd();

            expression = {
                type: "BinaryExpression",
                operator,
                left: expression,
                right
            };
        }

        return expression;
    }

    parseLogicalAnd() {
        let expression =
            this.parseEquality();

        while (this.match("&&")) {
            const operator =
                this.previous().value;

            const right =
                this.parseEquality();

            expression = {
                type: "BinaryExpression",
                operator,
                left: expression,
                right
            };
        }

        return expression;
    }

    parseEquality() {
        let expression =
            this.parseComparison();

        while (
            this.match(
                "==",
                "!=",
                "===",
                "!=="
            )
        ) {
            const operator =
                this.previous().value;

            const right =
                this.parseComparison();

            expression = {
                type: "BinaryExpression",
                operator,
                left: expression,
                right
            };
        }

        return expression;
    }

    parseComparison() {
        let expression =
            this.parseTerm();

        while (
            this.match(
                "<",
                "<=",
                ">",
                ">="
            )
        ) {
            const operator =
                this.previous().value;

            const right =
                this.parseTerm();

            expression = {
                type: "BinaryExpression",
                operator,
                left: expression,
                right
            };
        }

        return expression;
    }

    parseTerm() {
        let expression =
            this.parseFactor();

        while (
            this.match(
                "+",
                "-"
            )
        ) {
            const operator =
                this.previous().value;

            const right =
                this.parseFactor();

            expression = {
                type: "BinaryExpression",
                operator,
                left: expression,
                right
            };
        }

        return expression;
    }

    parseFactor() {
        let expression =
            this.parsePower();

        while (
            this.match(
                "*",
                "/",
                "%"
            )
        ) {
            const operator =
                this.previous().value;

            const right =
                this.parsePower();

            expression = {
                type: "BinaryExpression",
                operator,
                left: expression,
                right
            };
        }

        return expression;
    }

    parsePower() {
        let expression =
            this.parseUnary();

        if (this.match("**")) {
            const operator =
                this.previous().value;

            const right =
                this.parsePower();

            expression = {
                type: "BinaryExpression",
                operator,
                left: expression,
                right
            };
        }

        return expression;
    }

    parseUnary() {
        if (
            this.match(
                "!",
                "-",
                "+",
                "++",
                "--"
            )
        ) {
            const operator =
                this.previous().value;

            return {
                type: "UnaryExpression",
                operator,
                argument: this.parseUnary()
            };
        }

        /*
         * new Node()
         */

        if (this.match("new")) {
            const className =
                this.expectIdentifier(
                    "new-ge type hesaru beku guru."
                );

            this.expect(
                "(",
                "'(' beku guru."
            );

            const args = [];

            if (!this.check(")")) {
                do {
                    args.push(
                        this.parseExpression()
                    );
                } while (this.match(","));
            }

            this.expect(
                ")",
                "')' beku guru."
            );

            return {
                type: "NewExpression",
                className: className.value,
                arguments: args
            };
        }

        return this.parsePostfix();
    }

    parsePostfix() {
        let expression =
            this.parsePrimary();

        while (true) {

            /*
             * Function call
             */

            if (this.match("(")) {
                const args = [];

                if (!this.check(")")) {
                    do {
                        args.push(
                            this.parseExpression()
                        );
                    } while (this.match(","));
                }

                this.expect(
                    ")",
                    "')' beku guru."
                );

                expression = {
                    type: "CallExpression",
                    callee: expression,
                    arguments: args
                };

                continue;
            }

            /*
             * Array index
             */

            if (this.match("[")) {
                const index =
                    this.parseExpression();

                this.expect(
                    "]",
                    "']' beku guru."
                );

                expression = {
                    type: "IndexExpression",
                    object: expression,
                    index
                };

                continue;
            }

            /*
             * Member:
             *
             * node.data
             * node->data
             */

            if (
                this.match(
                    ".",
                    "->"
                )
            ) {
                const operator =
                    this.previous().value;

                const property =
                    this.expectIdentifier(
                        "Property hesaru beku guru."
                    );

                expression = {
                    type: "MemberExpression",
                    object: expression,
                    property: property.value,
                    operator
                };

                continue;
            }

            /*
             * postfix ++ / --
             */

            if (
                this.match(
                    "++",
                    "--"
                )
            ) {
                expression = {
                    type: "UpdateExpression",
                    operator:
                        this.previous().value,
                    argument: expression,
                    prefix: false
                };

                continue;
            }

            break;
        }

        return expression;
    }

    parsePrimary() {
        const token = this.peek();

        if (token.type === "number") {
            this.advance();

            return {
                type: "Literal",
                value: token.value
            };
        }

        if (token.type === "string") {
            this.advance();

            return {
                type: "Literal",
                value: token.value
            };
        }

        if (token.type === "identifier") {
            this.advance();

            if (token.value === "nijaa") {
                return {
                    type: "Literal",
                    value: true
                };
            }

            if (token.value === "sullu") {
                return {
                    type: "Literal",
                    value: false
                };
            }

            if (token.value === "illa") {
                return {
                    type: "Literal",
                    value: null
                };
            }

            return {
                type: "Identifier",
                name: token.value
            };
        }

        /*
         * Array literal
         */

        if (this.match("[")) {
            const elements = [];

            if (!this.check("]")) {
                do {
                    elements.push(
                        this.parseExpression()
                    );
                } while (this.match(","));
            }

            this.expect(
                "]",
                "']' beku guru."
            );

            return {
                type: "ArrayExpression",
                elements
            };
        }

        /*
         * Parenthesized expression
         */

        if (this.match("(")) {
            const expression =
                this.parseExpression();

            this.expect(
                ")",
                "')' beku guru."
            );

            return expression;
        }

        throw new SceneError(
            "AYYO GURU 😭 Expression sari illa.",
            token
        );
    }
}

/* =========================================================
   ENVIRONMENT
========================================================= */

class Environment {
    constructor(parent = null) {
        this.parent = parent;
        this.values = new Map();
    }

    declare(name, value) {
        this.values.set(name, value);
    }

    hasLocal(name) {
        return this.values.has(name);
    }

    has(name) {
        if (this.values.has(name)) {
            return true;
        }

        return this.parent
            ? this.parent.has(name)
            : false;
    }

    get(name) {
        if (this.values.has(name)) {
            return this.values.get(name);
        }

        if (this.parent) {
            return this.parent.get(name);
        }

        throw new Error(
            `Variable '${name}' siglilla guru.`
        );
    }

    assign(name, value) {
        if (this.values.has(name)) {
            this.values.set(name, value);
            return value;
        }

        if (this.parent) {
            return this.parent.assign(
                name,
                value
            );
        }

        throw new Error(
            `Variable '${name}' siglilla guru.`
        );
    }
}

/* =========================================================
   STRUCT INSTANCE
========================================================= */

class SceneInstance {
    constructor(structDef) {
        this.__sceneType = structDef.name;

        for (const field of structDef.fields) {
            this[field.name] = null;
        }
    }
}

/* =========================================================
   USER FUNCTION
========================================================= */

class SceneFunction {
    constructor(
        declaration,
        closure,
        interpreter
    ) {
        this.declaration = declaration;
        this.closure = closure;
        this.interpreter = interpreter;
    }

    async call(args) {
        if (
            this.interpreter.callDepth >=
            MAX_CALL_DEPTH
        ) {
            throw new Error(
                "AYYO GURU 😭 Maximum function recursion depth reached."
            );
        }

        this.interpreter.callDepth++;

        try {
            const env =
                new Environment(this.closure);

            const params =
                this.declaration.params;

            for (let i = 0; i < params.length; i++) {
                env.declare(
                    params[i].name,
                    args[i] ?? null
                );
            }

            try {
                await this.interpreter.execute(
                    this.declaration.body,
                    env
                );

                return null;

            } catch (signal) {

                if (
                    signal instanceof ReturnSignal
                ) {
                    return signal.value;
                }

                throw signal;
            }

        } finally {
            this.interpreter.callDepth--;
        }
    }
}

/* =========================================================
   INTERPRETER
========================================================= */

class Interpreter {
    constructor(ast) {
        this.ast = ast;
        this.global = new Environment();

        this.structs = new Map();

        this.callDepth = 0;

        this.loopCounter = 0;

        this.functions = new Map();

        this.installBuiltins();
    }

    /* =====================================================
       BUILTINS
    ===================================================== */

    installBuiltins() {

        /*
         * helu()
         */

        this.global.declare(
            "helu",
            (...args) => {
                console.log(
                    args
                        .map(v =>
                            this.formatValue(v)
                        )
                        .join(" ")
                );

                return null;
            }
        );

        /*
         * kelu()
         */

        this.global.declare(
            "kelu",
            async (...args) => {
                const prompt =
                    args.length > 0
                        ? args
                            .map(v =>
                                this.formatValue(v)
                            )
                            .join(" ")
                        : "En input beku guru? > ";

                return await this.input(
                    prompt
                );
            }
        );

        /*
         * Math / utility functions
         */

        this.global.declare(
            "len",
            value => {
                if (
                    value === null ||
                    value === undefined
                ) {
                    return 0;
                }

                if (
                    typeof value === "string" ||
                    Array.isArray(value)
                ) {
                    return value.length;
                }

                return 0;
            }
        );

        this.global.declare(
            "str",
            value =>
                this.formatValue(value)
        );

        this.global.declare(
            "num",
            value =>
                Number(value)
        );

        this.global.declare(
            "type",
            value => {
                if (value === null) {
                    return "null";
                }

                if (Array.isArray(value)) {
                    return "array";
                }

                if (
                    typeof value === "object" &&
                    value.__sceneType
                ) {
                    return value.__sceneType;
                }

                return typeof value;
            }
        );

        this.global.declare(
            "push",
            (array, value) => {
                if (!Array.isArray(array)) {
                    throw new Error(
                        "push() array mele maatra work agutte guru."
                    );
                }

                array.push(value);
                return array.length;
            }
        );

        this.global.declare(
            "pop",
            array => {
                if (!Array.isArray(array)) {
                    throw new Error(
                        "pop() array mele maatra work agutte guru."
                    );
                }

                return array.pop();
            }
        );

        this.global.declare(
            "abs",
            Math.abs
        );

        this.global.declare(
            "max",
            Math.max
        );

        this.global.declare(
            "min",
            Math.min
        );

        this.global.declare(
            "sqrt",
            Math.sqrt
        );

        this.global.declare(
            "floor",
            Math.floor
        );

        this.global.declare(
            "ceil",
            Math.ceil
        );

        this.global.declare(
            "round",
            Math.round
        );

        /*
         * scene namespace
         */

        const scene = {

            print: (...args) => {
                console.log(
                    args
                        .map(v =>
                            this.formatValue(v)
                        )
                        .join(" ")
                );

                return null;
            },

            input: async prompt => {
                return await this.input(
                    prompt ?? ""
                );
            },

            length: value => {
                if (
                    Array.isArray(value) ||
                    typeof value === "string"
                ) {
                    return value.length;
                }

                return 0;
            },

            sizeof: value => {
                if (value === null) {
                    return 0;
                }

                if (typeof value === "number") {
                    return 8;
                }

                if (typeof value === "boolean") {
                    return 1;
                }

                if (typeof value === "string") {
                    return value.length;
                }

                return 8;
            },

            cast: (type, value) => {
                switch (String(type)) {
                    case "number":
                        return Number(value);

                    case "string":
                        return String(value);

                    case "bool":
                    case "boolean":
                        return Boolean(value);

                    default:
                        return value;
                }
            }
        };

        this.global.declare(
            "scene",
            scene
        );
    }

    input(prompt = "") {
        return Promise.resolve(
            typeof window !== "undefined"
                ? window.prompt(String(prompt)) ?? ""
                : ""
        );
    }

    /* =====================================================
       RUN
    ===================================================== */

    async run() {

        /*
         * First pass:
         *
         * Register structs and functions before
         * executing statements.
         */

        this.registerDefinitions(
            this.ast.body,
            this.global
        );

        /*
         * Execute top-level non-definition statements.
         */

        for (const statement of this.ast.body) {

            if (
                statement.type ===
                    "FunctionDeclaration" ||
                statement.type ===
                    "StructDeclaration"
            ) {
                continue;
            }

            await this.execute(
                statement,
                this.global
            );
        }

        /*
         * Auto-run main()
         */

        const main =
            this.functions.get("main");

        if (main) {
            await main.call([]);
        }
    }

    registerDefinitions(
        statements,
        env
    ) {
        for (const statement of statements) {

            if (
                statement.type ===
                "StructDeclaration"
            ) {
                this.structs.set(
                    statement.name,
                    statement
                );
            }

            if (
                statement.type ===
                "FunctionDeclaration"
            ) {
                const fn =
                    new SceneFunction(
                        statement,
                        env,
                        this
                    );

                this.functions.set(
                    statement.name,
                    fn
                );

                /*
                 * Also expose it through the
                 * environment for function calls.
                 */

                env.declare(
                    statement.name,
                    fn
                );
            }
        }
    }

    /* =====================================================
       EXECUTION
    ===================================================== */

    async execute(
        statement,
        env
    ) {
        if (!statement) {
            return null;
        }

        switch (statement.type) {

            case "Program": {
                return await this.executeBlock(
                    statement.body,
                    env
                );
            }

            case "EmptyStatement":
                return null;

            case "BlockStatement": {
                const blockEnv =
                    new Environment(env);

                return await this.executeBlock(
                    statement.body,
                    blockEnv
                );
            }

            case "VariableDeclaration": {
                let value = null;

                if (statement.initializer) {
                    value =
                        await this.evaluate(
                            statement.initializer,
                            env
                        );
                }

                env.declare(
                    statement.name,
                    value
                );

                return value;
            }

            case "StructDeclaration":
                return null;

            case "FunctionDeclaration":
                return null;

            case "ExpressionStatement":
                return await this.evaluate(
                    statement.expression,
                    env
                );

            case "IfStatement": {

                if (
                    this.isTruthy(
                        await this.evaluate(
                            statement.condition,
                            env
                        )
                    )
                ) {
                    return await this.execute(
                        statement.thenBranch,
                        env
                    );
                }

                for (
                    const branch of
                    statement.elseIfBranches
                ) {
                    if (
                        this.isTruthy(
                            await this.evaluate(
                                branch.condition,
                                env
                            )
                        )
                    ) {
                        return await this.execute(
                            branch.body,
                            env
                        );
                    }
                }

                if (statement.elseBranch) {
                    return await this.execute(
                        statement.elseBranch,
                        env
                    );
                }

                return null;
            }

            case "WhileStatement": {

                let iterations = 0;

                while (
                    this.isTruthy(
                        await this.evaluate(
                            statement.condition,
                            env
                        )
                    )
                ) {
                    iterations++;

                    if (
                        iterations >
                        MAX_LOOP_ITERATIONS
                    ) {
                        throw new Error(
                            "AYYO GURU 😭 Infinite loop detected."
                        );
                    }

                    try {
                        await this.execute(
                            statement.body,
                            new Environment(env)
                        );
                    } catch (signal) {

                        if (
                            signal instanceof
                            BreakSignal
                        ) {
                            break;
                        }

                        if (
                            signal instanceof
                            ContinueSignal
                        ) {
                            continue;
                        }

                        throw signal;
                    }
                }

                return null;
            }

            case "ForStatement": {

                const loopEnv =
                    new Environment(env);

                if (
                    statement.initializer
                ) {
                    if (
                        statement.initializer
                            .type ===
                        "VariableDeclaration"
                    ) {
                        await this.execute(
                            statement.initializer,
                            loopEnv
                        );
                    } else {
                        await this.evaluate(
                            statement.initializer,
                            loopEnv
                        );
                    }
                }

                let iterations = 0;

                while (true) {

                    if (
                        statement.condition
                    ) {
                        const condition =
                            await this.evaluate(
                                statement.condition,
                                loopEnv
                            );

                        if (
                            !this.isTruthy(
                                condition
                            )
                        ) {
                            break;
                        }
                    }

                    iterations++;

                    if (
                        iterations >
                        MAX_LOOP_ITERATIONS
                    ) {
                        throw new Error(
                            "AYYO GURU 😭 Infinite loop detected."
                        );
                    }

                    try {
                        await this.execute(
                            statement.body,
                            new Environment(loopEnv)
                        );
                    } catch (signal) {

                        if (
                            signal instanceof
                            BreakSignal
                        ) {
                            break;
                        }

                        if (
                            signal instanceof
                            ContinueSignal
                        ) {
                            /*
                             * Continue still needs
                             * the for increment.
                             */
                        } else {
                            throw signal;
                        }
                    }

                    if (
                        statement.increment
                    ) {
                        await this.evaluate(
                            statement.increment,
                            loopEnv
                        );
                    }
                }

                return null;
            }

            case "DoWhileStatement": {

                let iterations = 0;

                do {
                    iterations++;

                    if (
                        iterations >
                        MAX_LOOP_ITERATIONS
                    ) {
                        throw new Error(
                            "AYYO GURU 😭 Infinite loop detected."
                        );
                    }

                    try {
                        await this.execute(
                            statement.body,
                            new Environment(env)
                        );
                    } catch (signal) {

                        if (
                            signal instanceof
                            BreakSignal
                        ) {
                            break;
                        }

                        if (
                            signal instanceof
                            ContinueSignal
                        ) {
                            /* continue to condition */
                        } else {
                            throw signal;
                        }
                    }

                } while (
                    this.isTruthy(
                        await this.evaluate(
                            statement.condition,
                            env
                        )
                    )
                );

                return null;
            }

            case "ReturnStatement": {

                let value = null;

                if (statement.value) {
                    value =
                        await this.evaluate(
                            statement.value,
                            env
                        );
                }

                throw new ReturnSignal(
                    value
                );
            }

            case "BreakStatement":
                throw new BreakSignal();

            case "ContinueStatement":
                throw new ContinueSignal();

            default:
                throw new Error(
                    `Unknown AST node '${statement.type}'.`
                );
        }
    }

    async executeBlock(
        statements,
        env
    ) {
        let result = null;

        for (const statement of statements) {
            result =
                await this.execute(
                    statement,
                    env
                );
        }

        return result;
    }

    /* =====================================================
       EVALUATION
    ===================================================== */

    async evaluate(
        expression,
        env
    ) {
        if (!expression) {
            return null;
        }

        switch (expression.type) {

            case "Literal":
                return expression.value;

            case "Identifier":
                return env.get(
                    expression.name
                );

            case "ArrayExpression": {
                const array = [];

                for (
                    const element of
                    expression.elements
                ) {
                    array.push(
                        await this.evaluate(
                            element,
                            env
                        )
                    );
                }

                return array;
            }

            case "BinaryExpression":
                return await this.evaluateBinary(
                    expression,
                    env
                );

            case "UnaryExpression":
                return await this.evaluateUnary(
                    expression,
                    env
                );

            case "UpdateExpression":
                return await this.evaluateUpdate(
                    expression,
                    env
                );

            case "AssignmentExpression":
                return await this.evaluateAssignment(
                    expression,
                    env
                );

            case "TernaryExpression": {
                const condition =
                    await this.evaluate(
                        expression.condition,
                        env
                    );

                if (
                    this.isTruthy(condition)
                ) {
                    return await this.evaluate(
                        expression.consequent,
                        env
                    );
                }

                return await this.evaluate(
                    expression.alternate,
                    env
                );
            }

            case "MemberExpression": {
                const object =
                    await this.evaluate(
                        expression.object,
                        env
                    );

                return this.getMember(
                    object,
                    expression.property
                );
            }

            case "IndexExpression": {
                const object =
                    await this.evaluate(
                        expression.object,
                        env
                    );

                const index =
                    await this.evaluate(
                        expression.index,
                        env
                    );

                return this.getIndex(
                    object,
                    index
                );
            }

            case "CallExpression":
                return await this.evaluateCall(
                    expression,
                    env
                );

            case "NewExpression":
                return await this.evaluateNew(
                    expression,
                    env
                );

            default:
                throw new Error(
                    `Unknown expression '${expression.type}'.`
                );
        }
    }

    async evaluateBinary(
        expression,
        env
    ) {
        const operator =
            expression.operator;

        /*
         * Short-circuit logical operators
         */

        if (operator === "&&") {
            const left =
                await this.evaluate(
                    expression.left,
                    env
                );

            if (!this.isTruthy(left)) {
                return false;
            }

            const right =
                await this.evaluate(
                    expression.right,
                    env
                );

            return this.isTruthy(right);
        }

        if (operator === "||") {
            const left =
                await this.evaluate(
                    expression.left,
                    env
                );

            if (this.isTruthy(left)) {
                return true;
            }

            const right =
                await this.evaluate(
                    expression.right,
                    env
                );

            return this.isTruthy(right);
        }

        const left =
            await this.evaluate(
                expression.left,
                env
            );

        const right =
            await this.evaluate(
                expression.right,
                env
            );

        switch (operator) {

            case "+":
                return left + right;

            case "-":
                return Number(left) - Number(right);

            case "*":
                return Number(left) * Number(right);

            case "/":
                return Number(left) / Number(right);

            case "%":
                return Number(left) % Number(right);

            case "**":
                return Number(left) ** Number(right);

            case "<":
                return left < right;

            case "<=":
                return left <= right;

            case ">":
                return left > right;

            case ">=":
                return left >= right;

            case "==":
            case "===":
                return left === right;

            case "!=":
            case "!==":
                return left !== right;

            default:
                throw new Error(
                    `Operator '${operator}' supported illa guru.`
                );
        }
    }

    async evaluateUnary(
        expression,
        env
    ) {
        const operator =
            expression.operator;

        if (
            operator === "++" ||
            operator === "--"
        ) {
            return await this.updateTarget(
                expression.argument,
                env,
                operator,
                true
            );
        }

        const value =
            await this.evaluate(
                expression.argument,
                env
            );

        switch (operator) {

            case "!":
                return !this.isTruthy(value);

            case "-":
                return -Number(value);

            case "+":
                return Number(value);

            default:
                throw new Error(
                    `Unary operator '${operator}' supported illa guru.`
                );
        }
    }

    async evaluateUpdate(
        expression,
        env
    ) {
        return await this.updateTarget(
            expression.argument,
            env,
            expression.operator,
            expression.prefix
        );
    }

    async updateTarget(
        target,
        env,
        operator,
        prefix
    ) {
        const oldValue =
            await this.evaluate(
                target,
                env
            );

        const newValue =
            operator === "++"
                ? Number(oldValue) + 1
                : Number(oldValue) - 1;

        await this.assignTarget(
            target,
            newValue,
            env
        );

        return prefix
            ? newValue
            : oldValue;
    }

    async evaluateAssignment(
        expression,
        env
    ) {
        const right =
            await this.evaluate(
                expression.right,
                env
            );

        if (
            expression.operator === "="
        ) {
            await this.assignTarget(
                expression.left,
                right,
                env
            );

            return right;
        }

        const oldValue =
            await this.evaluate(
                expression.left,
                env
            );

        let value;

        switch (expression.operator) {

            case "+=":
                value = oldValue + right;
                break;

            case "-=":
                value =
                    Number(oldValue) -
                    Number(right);
                break;

            case "*=":
                value =
                    Number(oldValue) *
                    Number(right);
                break;

            case "/=":
                value =
                    Number(oldValue) /
                    Number(right);
                break;

            case "%=":
                value =
                    Number(oldValue) %
                    Number(right);
                break;

            default:
                throw new Error(
                    `Assignment '${expression.operator}' supported illa guru.`
                );
        }

        await this.assignTarget(
            expression.left,
            value,
            env
        );

        return value;
    }

    async assignTarget(
        target,
        value,
        env
    ) {
        if (
            target.type ===
            "Identifier"
        ) {
            return env.assign(
                target.name,
                value
            );
        }

        if (
            target.type ===
            "MemberExpression"
        ) {
            const object =
                await this.evaluate(
                    target.object,
                    env
                );

            if (
                object === null ||
                object === undefined
            ) {
                throw new Error(
                    `Property '${target.property}' mele null guru.`
                );
            }

            object[target.property] =
                value;

            return value;
        }

        if (
            target.type ===
            "IndexExpression"
        ) {
            const object =
                await this.evaluate(
                    target.object,
                    env
                );

            const index =
                await this.evaluate(
                    target.index,
                    env
                );

            if (
                object === null ||
                object === undefined
            ) {
                throw new Error(
                    "Index target illa guru."
                );
            }

            object[index] = value;

            return value;
        }

        throw new Error(
            "Assignment target sari illa guru."
        );
    }

    /* =====================================================
       CALLS
    ===================================================== */

    async evaluateCall(
        expression,
        env
    ) {
        const args = [];

        for (
            const argument of
            expression.arguments
        ) {
            args.push(
                await this.evaluate(
                    argument,
                    env
                )
            );
        }

        /*
         * Function call:
         *
         * foo(...)
         */

        if (
            expression.callee.type ===
            "Identifier"
        ) {
            const name =
                expression.callee.name;

            /*
             * Struct constructor:
             *
             * Node()
             *
             * Also works for new Node()
             */

            if (this.structs.has(name)) {
                return this.instantiateStruct(
                    name
                );
            }

            const fn =
                env.get(name);

            return await this.callValue(
                fn,
                args,
                name
            );
        }

        /*
         * Member call:
         *
         * array.push()
         * array.pop()
         * array.size()
         * scene.print()
         */

        if (
            expression.callee.type ===
            "MemberExpression"
        ) {
            const member =
                expression.callee;

            const object =
                await this.evaluate(
                    member.object,
                    env
                );

            return await this.callMember(
                object,
                member.property,
                args
            );
        }

        const fn =
            await this.evaluate(
                expression.callee,
                env
            );

        return await this.callValue(
            fn,
            args,
            "<expression>"
        );
    }

    async callValue(
        fn,
        args,
        name
    ) {
        if (
            fn instanceof SceneFunction
        ) {
            return await fn.call(args);
        }

        if (
            typeof fn === "function"
        ) {
            return await fn(...args);
        }

        throw new Error(
            `'${name}' function alla guru.`
        );
    }

    async callMember(
        object,
        property,
        args
    ) {
        if (
            object === null ||
            object === undefined
        ) {
            throw new Error(
                `Property '${property}' mele null guru.`
            );
        }

        /*
         * Arrays
         */

        if (Array.isArray(object)) {

            switch (property) {

                case "push":
                    return object.push(
                        ...args
                    );

                case "pop":
                    return object.pop();

                case "size":
                case "length":
                    if (property === "size") {
                        return object.length;
                    }

                    return object.length;

                case "shift":
                    return object.shift();

                case "unshift":
                    return object.unshift(
                        ...args
                    );

                case "slice":
                    return object.slice(
                        ...args
                    );

                case "join":
                    return object.join(
                        ...args
                    );

                case "indexOf":
                    return object.indexOf(
                        ...args
                    );

                case "includes":
                    return object.includes(
                        ...args
                    );

                case "reverse":
                    object.reverse();
                    return object;

                default:
                    throw new Error(
                        `Array property '${property}' siglilla guru.`
                    );
            }
        }

        /*
         * Strings
         */

        if (typeof object === "string") {

            switch (property) {

                case "length":
                    return object.length;

                case "toUpperCase":
                    return object.toUpperCase();

                case "toLowerCase":
                    return object.toLowerCase();

                case "includes":
                    return object.includes(
                        ...args
                    );

                case "substring":
                    return object.substring(
                        ...args
                    );

                case "slice":
                    return object.slice(
                        ...args
                    );

                case "charAt":
                    return object.charAt(
                        ...args
                    );

                default:
                    throw new Error(
                        `String property '${property}' siglilla guru.`
                    );
            }
        }

        /*
         * Normal object method
         */

        const value =
            object[property];

        if (
            typeof value === "function"
        ) {
            return await value.apply(
                object,
                args
            );
        }

        throw new Error(
            `Property '${property}' callable alla guru.`
        );
    }

    async evaluateNew(
        expression,
        env
    ) {
        if (
            !this.structs.has(
                expression.className
            )
        ) {
            throw new Error(
                `Struct '${expression.className}' siglilla guru.`
            );
        }

        /*
         * Evaluate arguments even though the
         * current struct model does not require
         * constructor parameters.
         */

        for (
            const arg of
            expression.arguments
        ) {
            await this.evaluate(
                arg,
                env
            );
        }

        return this.instantiateStruct(
            expression.className
        );
    }

    instantiateStruct(name) {
        const struct =
            this.structs.get(name);

        return new SceneInstance(
            struct
        );
    }

    /* =====================================================
       MEMBERS
    ===================================================== */

    getMember(
        object,
        property
    ) {
        if (
            object === null ||
            object === undefined
        ) {
            throw new Error(
                `Property '${property}' mele null guru.`
            );
        }

        /*
         * Array properties
         */

        if (Array.isArray(object)) {

            if (
                property === "length" ||
                property === "size"
            ) {
                return object.length;
            }

            /*
             * expose a callable marker;
             * actual member calls are handled
             * separately in evaluateCall().
             */

            if (
                [
                    "push",
                    "pop",
                    "shift",
                    "unshift",
                    "slice",
                    "join",
                    "indexOf",
                    "includes",
                    "reverse"
                ].includes(property)
            ) {
                return (...args) =>
                    this.callMember(
                        object,
                        property,
                        args
                    );
            }

            return object[property];
        }

        /*
         * Strings
         */

        if (typeof object === "string") {
            if (property === "length") {
                return object.length;
            }

            return object[property];
        }

        /*
         * Normal object / SceneInstance
         */

        if (
            Object.prototype.hasOwnProperty.call(
                object,
                property
            )
        ) {
            return object[property];
        }

        if (
            property in object
        ) {
            return object[property];
        }

        throw new Error(
            `Property '${property}' siglilla guru.`
        );
    }

    getIndex(
        object,
        index
    ) {
        if (
            object === null ||
            object === undefined
        ) {
            throw new Error(
                "Index object illa guru."
            );
        }

        return object[index];
    }

    /* =====================================================
       HELPERS
    ===================================================== */

    isTruthy(value) {
        return Boolean(value);
    }

    formatValue(value) {
        if (value === null) {
            return "illa";
        }

        if (value === undefined) {
            return "undefined";
        }

        if (Array.isArray(value)) {
            return "[" +
                value
                    .map(v =>
                        this.formatValue(v)
                    )
                    .join(", ") +
                "]";
        }

        if (
            typeof value === "object" &&
            value.__sceneType
        ) {
            const fields = [];

            for (
                const key of
                Object.keys(value)
            ) {
                if (
                    key === "__sceneType"
                ) {
                    continue;
                }

                fields.push(
                    `${key}: ${this.formatValue(
                        value[key]
                    )}`
                );
            }

            return `${value.__sceneType}{${fields.join(", ")}}`;
        }

        return String(value);
    }
}

/* =========================================================
   C++ DETECTION
========================================================= */

function looksLikeCpp(source) {
    if (typeof source !== "string") {
        return false;
    }

    /*
     * Remove comments before detection.
     *
     * This prevents comments containing C++
     * words from triggering the detector.
     */

    const s = source
        .replace(
            /\/\*[\s\S]*?\*\//g,
            ""
        )
        .replace(
            /\/\/.*$/gm,
            ""
        )
        .trim();

    if (!s) {
        return false;
    }

    /*
     * Preprocessor
     */

    if (
        /^\s*#\s*include\b/m.test(s)
    ) {
        return true;
    }

    if (
        /^\s*#\s*define\b/m.test(s)
    ) {
        return true;
    }

    if (
        /^\s*#\s*ifdef\b/m.test(s)
    ) {
        return true;
    }

    if (
        /^\s*#\s*ifndef\b/m.test(s)
    ) {
        return true;
    }

    if (
        /^\s*#\s*pragma\b/m.test(s)
    ) {
        return true;
    }

    /*
     * std
     */

    if (
        /\busing\s+namespace\s+std\b/.test(s)
    ) {
        return true;
    }

    if (
        /\bstd\s*::/.test(s)
    ) {
        return true;
    }

    /*
     * Streams
     */

    if (
        /\bcout\s*<</.test(s)
    ) {
        return true;
    }

    if (
        /\bcin\s*>>/.test(s)
    ) {
        return true;
    }

    if (
        /\bcerr\s*<</.test(s)
    ) {
        return true;
    }

    if (
        /\bclog\s*<</.test(s)
    ) {
        return true;
    }

    /*
     * C++ null
     */

    if (
        /\bnullptr\b/.test(s)
    ) {
        return true;
    }

    /*
     * C++ main
     */

    if (
        /\bint\s+main\s*\(/.test(s)
    ) {
        return true;
    }

    if (
        /\bvoid\s+main\s*\(/.test(s)
    ) {
        return true;
    }

    /*
     * class
     */

    if (
        /\bclass\s+[A-Za-z_]\w*\s*\{/.test(s)
    ) {
        return true;
    }

    /*
     * struct
     *
     * Scene++ uses "vastu", so normal C++
     * struct syntax is safe to detect.
     */

    if (
        /\bstruct\s+[A-Za-z_]\w*\s*\{/.test(s)
    ) {
        return true;
    }

    /*
     * C++ casts
     */

    if (
        /\bstatic_cast\s*</.test(s)
    ) {
        return true;
    }

    if (
        /\bdynamic_cast\s*</.test(s)
    ) {
        return true;
    }

    if (
        /\bconst_cast\s*</.test(s)
    ) {
        return true;
    }

    if (
        /\breinterpret_cast\s*</.test(s)
    ) {
        return true;
    }

    /*
     * delete
     */

    if (
        /\bdelete\s+[A-Za-z_]\w*/.test(s)
    ) {
        return true;
    }

    /*
     * Scope operator
     */

    if (
        /\b[A-Za-z_]\w*\s*::\s*[A-Za-z_]\w*/.test(s)
    ) {
        return true;
    }

    /*
     * templates
     */

    if (
        /\btemplate\s*</.test(s)
    ) {
        return true;
    }

    return false;
}

/* =========================================================
   INPUT CLEANING
========================================================= */

function cleanInteractiveSource(source) {
    return source
        .replace(
            /^\s*={5,}.*$/gm,
            ""
        )
        .replace(
            /^\s*🚀.*$/gm,
            ""
        )
        .replace(
            /^\s*"Swalpa code.*$/gm,
            ""
        )
        .trim();
}

/* =========================================================
   RUN SOURCE
========================================================= */

async function executeSource(source) {

    const cleaned =
        cleanInteractiveSource(
            source
        );

    if (!cleaned) {
        throw new Error(
            "Scene++ program empty ide guru."
        );
    }

    /*
     * C++ guard
     */

    if (
        looksLikeCpp(cleaned)
    ) {
        throw new Error(
            "AYYO GURU 😭 Idhu C++ tara ide. " +
            "First C++ → Scene++ converter use maadi."
        );
    }

    let tokens;

    try {
        tokens =
            new Lexer(cleaned)
                .tokenize();
    } catch (error) {
        throw error;
    }

    let ast;

    try {
        ast =
            new Parser(tokens)
                .parseProgram();
    } catch (error) {
        throw error;
    }

    const interpreter =
        new Interpreter(ast);

    await interpreter.run();
}

/* =========================================================
   ERROR DISPLAY
========================================================= */

function displayError(error) {

    console.error("");

    console.error(
        "AYYO GURU 😭"
    );

    if (error instanceof SceneError) {
        console.error(
            error.message
        );
    } else {
        console.error(
            error?.message ||
            String(error)
        );
    }

    console.error("");

    console.error(
        "Swalpa syntax check maadi."
    );

    console.error("");

    console.error(
        "=== Code Execution Failed guru! ==="
    );

    console.error("");

    console.error(
        "=== Code Exited With Errors ==="
    );
}

/* =========================================================
   SUCCESS DISPLAY
========================================================= */

function displaySuccess() {

    console.log("");

    console.log(
        "=== Code Execution Successful guru! 🔥 ==="
    );

    console.log(
        "Scene complete. Swalpa mass! 😎"
    );
}

/* =========================================================
   HEADER
========================================================= */

function printHeader() {

    console.log(
        "============================================================"
    );

    console.log(
        "                     🚀 SCENE++"
    );

    console.log(
        "                Bengaluru Edition"
    );

    console.log(
        "============================================================"
    );

    console.log("");

    console.log(
        '              "Swalpa code, full scene."'
    );

    console.log("");

    console.log(
        "Enter your Scene++ program below."
    );

    console.log(
        "When finished, type:"
    );

    console.log("");

    console.log(
        "    END"
    );

    console.log("");

    console.log(
        "============================================================"
    );

    console.log("");
}

/* =========================================================
   FILE MODE
========================================================= */

async function runFile(filename) {

    if (!fs.existsSync(filename)) {
        console.error(
            `AYYO GURU 😭 File '${filename}' siglilla.`
        );

        process.exitCode = 1;
        return;
    }

    let source;

    try {
        source =
            fs.readFileSync(
                filename,
                "utf8"
            );
    } catch (error) {
        console.error(
            "AYYO GURU 😭 File read aglilla."
        );

        console.error(
            error.message
        );

        process.exitCode = 1;
        return;
    }

    try {
        await executeSource(source);

        /*
         * IMPORTANT:
         * Success only happens if the complete
         * program finished without an exception.
         */

        displaySuccess();

    } catch (error) {

        displayError(error);

        /*
         * IMPORTANT:
         * No success message after failure.
         */

        process.exitCode = 1;
        return;
    }
}

/* =========================================================
   INTERACTIVE MODE
========================================================= */

async function runInteractive() {

    printHeader();

    const rl =
        readline.createInterface({
            input: process.stdin,
            output: process.stdout,
            terminal: true
        });

    const lines = [];

    return new Promise(resolve => {

        rl.on(
            "line",
            async line => {

                /*
                 * END must be exactly recognized.
                 */

                if (
                    line.trim() === "END"
                ) {
                    rl.close();

                    const source =
                        lines.join("\n");

                    try {

                        await executeSource(
                            source
                        );

                        displaySuccess();

                    } catch (error) {

                        displayError(
                            error
                        );

                        /*
                         * NEVER print success here.
                         */

                        process.exitCode = 1;
                    }

                    resolve();
                    return;
                }

                lines.push(line);
            }
        );

        rl.on(
            "close",
            () => {
                /*
                 * If Ctrl+D / EOF occurs without
                 * END, still attempt execution only
                 * when source exists.
                 */

                if (lines.length === 0) {
                    resolve();
                }
            }
        );
    });
}

/* =========================================================
   MAIN CLI
========================================================= */

async function main() {

    const args =
        process.argv.slice(2);

    /*
     * File mode:
     *
     * node scene.js program.scene
     */

    if (args.length > 0) {
        await runFile(args[0]);
        return;
    }

    /*
     * Interactive mode:
     *
     * node scene.js
     */

    await runInteractive();
}

/* =========================================================
   START
========================================================= */

if (false) {
    main().catch(error => {

        /*
         * Final safety net.
         *
         * Even unexpected errors must NEVER
         * produce a success message.
         */

        displayError(error);

        process.exitCode = 1;
    });
}

/* =========================================================
   EXPORTS
========================================================= */

export {
    Lexer,
    Parser,
    Interpreter,
    Environment,
    SceneFunction,
    SceneInstance,
    SceneError,
    looksLikeCpp,
    executeSource
};

